/**
 * SEO indexing policy for programmatic pages.
 *
 * Context: Google flagged the site for "low value content" because ~22k
 * near-duplicate programmatic salary/trends pages were indexed. The fix is NOT
 * "more pages" — it is a curated indexable surface where each page carries
 * unique wage figures, location context, and occupation-family editorial.
 *
 * Indexable = on the curated occupation list AND national/state location.
 * MSA pages and non-curated occupations stay reachable as SPA routes with
 * noindex and are excluded from the sitemap.
 *
 * IMPORTANT: Do NOT gate indexation on enrichment API success. July 2026:
 * tying noindex to `has_enriched_content` baked robots=noindex into 1,100+
 * curated salary pages (missing O*NET/projections for some SOCs, or slow
 * fetches during prerender). Enrichment improves content quality; it must
 * not decide whether a curated page is allowed in the index.
 *
 * Target sitemap size after a clean production build: ~3,680 URLs
 * (50 salary × 52 + 15 trends × 52 + 50 best-states + hubs/indexes, incl.
 * /highest-paying/*, /occupations/, /states/).
 * Do not expand beyond that set until the curated catalogue is indexed.
 *
 * IMPORTANT: scripts/generate-prerender-routes.js and scripts/generate-sitemap.js
 * parse the arrays below by regex. Keep the `export const NAME = [ ... ];`
 * format with one quoted slug per line.
 */

import { isValidState } from './stateUtils';

/**
 * Occupations whose /salary/{occ}/{state} and /best-states/{occ} pages remain
 * indexed. Curated mix of the highest-employment occupations and
 * high-search-intent roles. Slugs must match generateOccupationSlug(occ_title).
 */
export const INDEXED_SALARY_OCCUPATIONS = [
  "registered-nurses",
  "software-developers",
  "retail-salespersons",
  "cashiers",
  "customer-service-representatives",
  "general-and-operations-managers",
  "heavy-and-tractor-trailer-truck-drivers",
  "fast-food-and-counter-workers",
  "office-clerks-general",
  "stockers-and-order-fillers",
  "waiters-and-waitresses",
  "accountants-and-auditors",
  "elementary-and-middle-school-teachers",
  "secondary-school-teachers",
  "teaching-assistants",
  "nursing-assistants",
  "medical-assistants",
  "home-health-and-personal-care-aides",
  "pharmacy-technicians",
  "physicians",
  "therapists",
  "social-workers",
  "security-guards",
  "police-and-sheriffs-patrol-officers",
  "electricians",
  "carpenters",
  "construction-laborers",
  "maintenance-and-repair-workers-general",
  "automotive-service-technicians-and-mechanics",
  "industrial-machinery-mechanics",
  "welders-cutters-solderers-and-brazers",
  "janitors-and-cleaners-except-maids-and-housekeeping-cleaners",
  "maids-and-housekeeping-cleaners",
  "landscaping-and-groundskeeping-workers",
  "cooks-restaurant",
  "bartenders",
  "food-preparation-workers",
  "receptionists-and-information-clerks",
  "bookkeeping-accounting-and-auditing-clerks",
  "financial-managers",
  "financial-analysts-and-advisors",
  "human-resources-specialists",
  "market-research-analysts-and-marketing-specialists",
  "management-analysts",
  "project-management-specialists",
  "computer-user-support-specialists",
  "computer-and-information-systems-managers",
  "sales-managers",
  "lawyers",
  "light-truck-drivers",
];

/**
 * Subset of occupations whose /trends/{occ}/{state} pages remain indexed.
 * Trends pages largely repeat salary-page data in chart form, so we keep the
 * indexed set smaller during review.
 */
export const INDEXED_TRENDS_OCCUPATIONS = [
  "registered-nurses",
  "software-developers",
  "retail-salespersons",
  "cashiers",
  "customer-service-representatives",
  "general-and-operations-managers",
  "heavy-and-tractor-trailer-truck-drivers",
  "accountants-and-auditors",
  "elementary-and-middle-school-teachers",
  "medical-assistants",
  "electricians",
  "police-and-sheriffs-patrol-officers",
  "financial-managers",
  "human-resources-specialists",
  "lawyers",
];

/** Expected curated programmatic page counts (national + 50 states + DC = 52). */
export const INDEXING_TARGETS = {
  locationsPerOccupation: 52,
  salaryOccupations: INDEXED_SALARY_OCCUPATIONS.length,
  trendsOccupations: INDEXED_TRENDS_OCCUPATIONS.length,
  salaryPages: INDEXED_SALARY_OCCUPATIONS.length * 52,
  trendsPages: INDEXED_TRENDS_OCCUPATIONS.length * 52,
  bestStatesPages: INDEXED_SALARY_OCCUPATIONS.length,
  /** Hubs vary; programmatic curated floor is salary + trends + best-states. */
  programmaticFloor:
    INDEXED_SALARY_OCCUPATIONS.length * 52 +
    INDEXED_TRENDS_OCCUPATIONS.length * 52 +
    INDEXED_SALARY_OCCUPATIONS.length,
};

const salaryIndexSet = new Set(INDEXED_SALARY_OCCUPATIONS);
const trendsIndexSet = new Set(INDEXED_TRENDS_OCCUPATIONS);

/** National and state-level pages are indexable; MSA/metro pages are not. */
export const isIndexableLocation = (location?: string): boolean => {
  if (!location) return false;
  const slug = location.toLowerCase();
  return slug === 'united-states' || slug === 'national' || isValidState(slug);
};

/**
 * @deprecated Enrichment must not gate indexation. Kept so call sites that
 * still pass the option compile; the value is ignored.
 */
export interface IndexQualityOptions {
  hasEnrichedContent?: boolean;
}

export const shouldIndexSalaryPage = (
  occupationSlug?: string,
  location?: string,
  _opts: IndexQualityOptions = {}
): boolean => {
  if (!occupationSlug) return false;
  return (
    salaryIndexSet.has(occupationSlug.toLowerCase()) &&
    isIndexableLocation(location)
  );
};

export const shouldIndexTrendsPage = (
  occupationSlug?: string,
  location?: string,
  _opts: IndexQualityOptions = {}
): boolean => {
  if (!occupationSlug) return false;
  return (
    trendsIndexSet.has(occupationSlug.toLowerCase()) &&
    isIndexableLocation(location)
  );
};

export const shouldIndexBestStatesPage = (
  occupationSlug?: string,
  _opts: IndexQualityOptions = {}
): boolean => {
  if (!occupationSlug) return false;
  return salaryIndexSet.has(occupationSlug.toLowerCase());
};

/**
 * Returns true if a /salary/{occ}/{location} link is crawl-budget-worthy.
 * Use this to decide whether internal links should render as crawlable `<a>`
 * tags vs non-crawlable buttons/spans. Prevents Google from wasting crawl
 * budget on pages that carry noindex.
 */
export const isCrawlableSalaryLink = (
  occupationSlug?: string,
  locationSlug?: string
): boolean => {
  if (!occupationSlug) return false;
  return salaryIndexSet.has(occupationSlug.toLowerCase()) && isIndexableLocation(locationSlug);
};

export const isCrawlableTrendsLink = (
  occupationSlug?: string,
  locationSlug?: string
): boolean => {
  if (!occupationSlug) return false;
  return trendsIndexSet.has(occupationSlug.toLowerCase()) && isIndexableLocation(locationSlug);
};

export const isCrawlableBestStatesLink = (
  occupationSlug?: string
): boolean => {
  if (!occupationSlug) return false;
  return salaryIndexSet.has(occupationSlug.toLowerCase());
};
